import { Component, OnInit, AfterViewInit, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { Location, DatePipe } from '@angular/common';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { BatchingService } from './services/batching-service';
import { TrackingService } from './services/tracking.service';
import { TrackingFieldService } from './services/tracking-field.service';
import { Document, DocumentStatusType } from './models';
import { ResendDocument, TrackingSharedActionMethodsService } from './services/tracking-shared-action-methods.service';
import { LoadingPanelService, LoadingPanelSettings } from '../shared/loading-panel/loading-panel.service';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';
import { DateTimeService } from '../shared/datetime/datetime.service';
import { DataFieldGroup } from '../shared/datafields/models/datafield-group';
import { Guid } from '../shared/utils';
import { GenericModalService, ModalSettingsJsonViewer, ModalSettingsTemplate } from '../shared/generic-modal/generic-modal.service';
import { TestToolService } from '../test-tool/services/test-tool.service';
import { StatusEventSearchObject } from './models/status-event-search-object';
import { StatusEventSearchItem } from './models/status-event-search-item';
import { BatchInfo } from './models/batch-info';
import { MessageRelationType } from './models/message-relation-types';
import { MessageRelation } from './models/message-relation';
import { CancelMessageDialogComponent } from './cancel-messages-dialog/cancel-message-dialog.component';
import { AddInterchangeToTestCaseDialogComponent, AddInterchangeToTestCaseDialogResult } from './add-interchange-to-test-case-dialog/add-interchange-to-test-case-dialog.component';
import { ExceptionInstance } from '../error-handling/models/exception-instance';
import { TrackingUiAction } from '../developer/models/tracking-ui-action';
import { RunTrackingUiActionDialogComponent } from './run-tracking-ui-action-dialog/run-tracking-ui-action-dialog.component';
import { TrackingActionDocument } from './models/tracking-action-document';
import { TrackingActionValidationResultSingle } from './models/tracking-action-validate-result-single';
import { SvgIconComponent } from '../shared/icons/svg-icon.component';
import { FormsModule } from '@angular/forms';
import { TrackingStatusLabelComponent } from './tracking-status-label.component';
import { PermissionRequiredDirective } from '../shared/authentication/permission-required.directive';
import { TrackingFileInfoRowComponent } from './tracking-file-info-row.component';
import { TrackingErrorBoxComponent } from './tracking-error-box/tracking-error-box.component';
import { CollapsibleGroupComponent } from '../shared/collapsible-group/collapsible-group.component';
import { TrackingMessageRelationsRowComponent } from './tracking-message-relations-row/tracking-message-relations-row.component';
import { ContentShowerComponent } from '../shared/content-shower/content-shower.component';
import { RouterLinkComponent } from '../shared/router-link/router-link.component';
import { DocumentConfigLinksComponent } from '../distribution/document-config-links.component';
import { BackupFileDownloaderComponent } from '../shared/download/backup-file-downloader.component';
import { TrackingDocumentShowerComponent } from './tracking-document-shower.component';
import { DataFieldGroupContainerComponent } from '../shared/datafields/datafield-group-container.component';
import { DxDataGridModule } from 'devextreme-angular';
import { BizDataGridDirective } from '../shared/grid/biz-grid.directive';
import { DxiColumnModule } from 'devextreme-angular/ui/nested';
import { DxTemplateModule } from 'devextreme-angular/core';
import { StatusEventsMasterGridComponent } from './status-events-master-detail-grid/status-events-master-grid.component';
import { CurrentUserService } from '../user/services';
import { InitConfiguration } from '../distribution/models/init-configuration';
import { InitConfigurationDialogComponent } from '../distribution/init-configuration-dialog/init-configuration-dialog.component';

interface TrackingUiActionState {
    IsDisabled: boolean;
    Title: string;
}

@Component({
    templateUrl: './tracking-search-result-details.component.html',
    styleUrls: ['./tracking-search-result-details.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, FormsModule, TrackingStatusLabelComponent, PermissionRequiredDirective, TrackingFileInfoRowComponent, TrackingErrorBoxComponent, CollapsibleGroupComponent, TrackingMessageRelationsRowComponent, ContentShowerComponent, RouterLinkComponent, DocumentConfigLinksComponent, BackupFileDownloaderComponent, TrackingDocumentShowerComponent, DataFieldGroupContainerComponent, DxDataGridModule, BizDataGridDirective, DxiColumnModule, DxTemplateModule, StatusEventsMasterGridComponent, DatePipe]
})
export class TrackingSearchResultDetailsComponent implements AfterViewInit, OnInit {
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly location = inject(Location);
    private readonly currentUserService = inject(CurrentUserService);
    private readonly trackingService = inject(TrackingService);
    private readonly trackingFieldService = inject(TrackingFieldService);
    private readonly modalService = inject(GenericModalService);
    private readonly toastService = inject(ToastService);
    private readonly loadingPanelService = inject(LoadingPanelService);
    private readonly trackingSharedActionMethodsService = inject(TrackingSharedActionMethodsService);
    private readonly testToolService = inject(TestToolService);
    private readonly batchingService = inject(BatchingService);
    readonly dateTimeService = inject(DateTimeService);

    readonly additionalFilePageSize = 30;
    visibleDocumentAdditionalFileCount = this.additionalFilePageSize;
    visibleInterchangeInAdditionalFileCount = this.additionalFilePageSize;
    visibleInterchangeOutAdditionalFileCount = this.additionalFilePageSize;

    parms: Params = {};
    documents: Document[] = [];
    documentIds: number[] = [];
    interchangeInIds: number[] = [];

    readonly selectedDocumentSignal = signal<Document | undefined>(undefined);
    readonly hasSelectedDocumentInitConfigValues = computed(() => !!this.selectedDocumentSignal()?.InterchangeIn?.InitConfiguration);

    get selectedDocument(): Document | undefined {
        return this.selectedDocumentSignal();
    }

    loadingStatusTypes = false;
    selectedStatusTypes: DocumentStatusType[] = [];
    statusTypesForDocument: DocumentStatusType[] = [];
    showTechnicalStatus = false;
    showAdditionalFiles = false;
    showPartnerAllowed = false;
    showDistributionAllowed = false;
    showStatusEvent = false;

    exceptionInstances: ExceptionInstance[] = [];

    trackingFieldGroups: DataFieldGroup[] = [];
    statusEvents: StatusEventSearchItem[] | undefined;
    batchInfo: BatchInfo | null = null;
    showDocumentConfigInLinks = true;
    showDocumentConfigOutLinks = true;

    messageRelationTypes: MessageRelationType[] = [];

    trackingUiActions: TrackingUiAction[] = [];
    trackingUiActionStates: TrackingUiActionState[] = [];

    private isParamsInitialized = false;

    ngOnInit(): void {
        this.showPartnerAllowed = this.currentUserService.hasPermissions(['Partner_View_Search', 'Partner_Action_GeneralShow']);
        this.showDistributionAllowed = this.currentUserService.hasPermission('Distribution_View_Search');
        this.showStatusEvent = this.currentUserService.hasPermission('TrackingStatusEvent_View_Search');
        void this.InitAsync();
    }

    private async InitAsync(): Promise<void> {
        try {
            this.messageRelationTypes = await this.trackingService.getMessageRelationTypes();
            this.trackingUiActions = await this.trackingService.getTrackingUiActionsForDetailsPage();
            this.trackingUiActionStates = this.trackingUiActions.map(() => ({ IsDisabled: true, Title: 'No documents selected.' }));
            this.loadCustomActionButtons();
        }
        catch {
            // ignore
        }
    }

    ngAfterViewInit(): void {
        this.route.params.subscribe((parms: Params) => {
            // Some other components/links can navigate to this page while we are already on the page,
            // so we need to check if it's an internal reload (which it is when resending documents).
            const isInternalReload = (parms as { internalReload?: unknown; }).internalReload === 'true';

            if (this.isParamsInitialized && isInternalReload) {
                return; // If we already once have listened for the params - ignore it to prevent refreshing the data multiple times on eg. "Resend".
            }

            this.isParamsInitialized = true;

            this.parms = parms;
            this.documentIds = this.toIds(parms.documentIds);
            this.interchangeInIds = this.toIds(parms.interchangeInIds);

            this.ensureDocumentCap();

            void this.refresh();
        });
    }

    private toIds(ids: unknown): number[] {
        const source = typeof ids === 'string' ? ids : '';
        return source.length === 0 ? [] : source.split(',').map(id => Number(id)).filter(id => !Number.isNaN(id));
    }

    async refresh(selectedDocumentId?: number, selectedInterchangeId?: number): Promise<void> {
        this.visibleDocumentAdditionalFileCount = this.additionalFilePageSize; // Reset paging...
        this.visibleInterchangeInAdditionalFileCount = this.additionalFilePageSize;
        this.visibleInterchangeOutAdditionalFileCount = this.additionalFilePageSize;

        // Nothing to refresh
        if (this.documentIds.length === 0 && this.interchangeInIds.length === 0) {
            this.setSelectedDocument(undefined);
            void this.router.navigate(['/tracking']);
        }
        else {
            this.loadingPanelService.showLoadingPanel(new LoadingPanelSettings('Loading documents...'));
            try {
                const result = await this.trackingService.getDocuments(this.documentIds, this.interchangeInIds);
                this.documents = result.Documents;
                if (result) {
                    if (result.Documents.length > 0) {

                        if (selectedDocumentId) {
                            this.setSelectedDocument(this.documents.find((d) => d.DocumentId === selectedDocumentId));
                        }
                        else if (selectedInterchangeId) {
                            this.setSelectedDocument(this.documents.find((d) => d.InterchangeIn?.InterchangesId === selectedInterchangeId));
                        }
                        else if (this.selectedDocument) {
                            this.setSelectedDocument(this.documents.find((d) => d.DocumentId === this.selectedDocument?.DocumentId));
                        }

                        if (!this.selectedDocument) { // Set the first found document if no pre-conditions found a document.
                            this.setSelectedDocument(result.Documents[0]);
                        }

                        this.updateIdCollections();

                        if (result.ExceededMaxDocuments) {
                            this.showInterchangeHasTooManyDocumentsWarning();
                        }

                        this.hideStatusEvents();
                    }
                    else {
                        this.setSelectedDocument(undefined);
                        void this.router.navigate(['/tracking']);
                    }
                }
            }
            catch {
                // ignore
            }
            finally {
                this.loadingPanelService.hideLoadingPanel();
            }
        }
    }

    async refreshSelected(): Promise<void> {
        if (this.selectedDocument) {
            const documentIds = this.selectedDocument.DocumentId ? [this.selectedDocument.DocumentId] : [];
            const interchangeInIds = this.selectedDocument.DocumentId ? [] : [this.selectedDocument.InterchangeIn.InterchangesId];
            this.loadingPanelService.showLoadingPanel(new LoadingPanelSettings('Loading document...'));
            try {
                const result = await this.trackingService.getDocuments(documentIds, interchangeInIds, this.documents.length);
                if (result) {
                    if (result.Documents.length > 0) {
                        const loadedDocument = result.Documents[0];
                        let selectedDocumentIndex = 0;

                        if (loadedDocument.DocumentId === undefined || loadedDocument.DocumentId === null) {
                            selectedDocumentIndex = this.documents.findIndex((d) => d.InterchangeIn?.InterchangesId === this.selectedDocument?.InterchangeIn?.InterchangesId);
                        } else {
                            selectedDocumentIndex = this.documents.findIndex((d) => d.DocumentId === this.selectedDocument?.DocumentId);
                        }

                        if (selectedDocumentIndex >= 0) {
                            // Replace the object-reference in the array of documents...
                            this.documents.splice(selectedDocumentIndex, 1, ...result.Documents);
                        }

                        this.setSelectedDocument(loadedDocument);

                        this.updateIdCollections();

                        if (result.ExceededMaxDocuments) {
                            this.showInterchangeHasTooManyDocumentsWarning();
                        }

                        this.hideStatusEvents();
                    }
                }
            }
            finally {
                this.loadingPanelService.hideLoadingPanel();
            }
        }
    }

    async resend() {
        if (!this.selectedDocument) {
            return;
        }

        try {
            const resendDocument = { documentId: this.selectedDocument.DocumentId, interchangeInId: this.selectedDocument.InterchangeIn?.InterchangesId } as ResendDocument;
            const result = await this.trackingSharedActionMethodsService.resend([resendDocument], 1);
            this.documentIds.push(...result.ResentDocuments.map(d => d.DocumentIdReproduced));
            this.interchangeInIds.push(...result.ResentInterchangeIns.map(d => d.InterchangeInIdReproduced));

            const selectedDocumentId = result.ResentDocuments.length > 0 ? result.ResentDocuments[0].DocumentIdReproduced : undefined;
            const selectedInterchangeId = result.ResentInterchangeIns.length > 0 ? result.ResentInterchangeIns[0].InterchangeInIdReproduced : undefined;

            this.updateUrlWithoutNavigation('/tracking/result/details', { documentIds: this.documentIds.join(','), interchangeInIds: this.interchangeInIds.join(','), internalReload: true });
            void this.refresh(selectedDocumentId, selectedInterchangeId);
        }
        catch {
            // ignore
        }
    }

    async handleManually(): Promise<void> {
        if (this.selectedDocument) {
            const documentIds = this.selectedDocument.DocumentId ? [this.selectedDocument.DocumentId] : [];
            const interchangeInIds = this.selectedDocument.DocumentId ? [] : [this.selectedDocument.InterchangeIn.InterchangesId];
            try {
                await this.trackingSharedActionMethodsService.handleManually(documentIds, interchangeInIds);
                await this.refresh();
            }
            catch {
                // ignore
            }
        }
    }

    async setDocumentOnHold(): Promise<void> {
        if (this.selectedDocument) {
            try {
                const documentIds = this.selectedDocument.DocumentId ? [this.selectedDocument.DocumentId] : [];
                const interchangeInIds = this.selectedDocument.DocumentId ? [] : [this.selectedDocument.InterchangeIn.InterchangesId];
                await this.trackingSharedActionMethodsService.setDocumentsOnHold(documentIds, interchangeInIds);
                await this.refresh();
            }
            catch {
                // ignore
            }
        }
    }

    private recreate(): void {
        if (this.selectedDocument) {
            const documentIds = this.selectedDocument.DocumentId ? [this.selectedDocument.DocumentId] : [];
            const interchangeInIds = this.selectedDocument.DocumentId ? [] : [this.selectedDocument.InterchangeIn.InterchangesId];
            void this.trackingSharedActionMethodsService.recreate(documentIds, interchangeInIds).catch(() => { });
        }
    }

    async delete(): Promise<void> {
        if (this.selectedDocument) {
            const documentIds = this.selectedDocument.DocumentId ? [this.selectedDocument.DocumentId] : [];
            const interchangeInIds = this.selectedDocument.DocumentId ? [] : [this.selectedDocument.InterchangeIn.InterchangesId];
            try {
                await this.trackingSharedActionMethodsService.delete(documentIds, interchangeInIds);
                this.removeDocument(this.selectedDocument);
            }
            catch {
                // ignore
            }
        }
    }

    async changeStakeholder(): Promise<void> {
        if (this.selectedDocument) {
            const documentIds = this.selectedDocument.DocumentId ? [this.selectedDocument.DocumentId] : [];
            const interchangeInIds = this.selectedDocument.DocumentId ? [] : [this.selectedDocument.InterchangeIn.InterchangesId];
            let selectedStakeHolderId: number | null = null;

            if (this.exceptionInstances && this.exceptionInstances.length > 0) {
                selectedStakeHolderId = this.exceptionInstances[this.exceptionInstances.length - 1].Stakeholder?.StakeholdersId;
            }
            try {
                await this.trackingSharedActionMethodsService.changeStakeholder(documentIds, interchangeInIds, selectedStakeHolderId);
                await this.refresh();
            }
            catch {
                // ignore
            }
        }
    }

    setSelectedDocument(document: Document | undefined): void {
        this.selectedDocumentSignal.set(document);
        this.selectedDocumentChanged();
    }

    selectedDocumentChanged(): void {
        if (this.selectedDocument) {
            this.showHideDocumentConfigLinks();
            this.showHideTechnicalStatus(false);
            void this.findStatusTypes();
            void this.findExceptionInstances();
            void this.getBatchInfo();
            this.hideStatusEvents();
            this.loadCustomActionButtons();
        }
    }

    hideStatusEvents() {
        this.statusEvents = undefined;
    }

    async onShowInitConfig(): Promise<void> {
        try {
            const componentData = { initConfiguration: this.selectedDocumentSignal()?.InterchangeIn?.InitConfiguration, disabled: true };
            await this.modalService.openComponentType<InitConfiguration>(InitConfigurationDialogComponent, componentData, new ModalSettingsTemplate('lg', 'static'));
        }
        catch {
            // We get here when the dialog is dismissed (canceled)
        }
    }

    showHideTechnicalStatus(showTechnicalStatus: boolean) {
        if (this.selectedDocument) {
            this.selectedStatusTypes = this.statusTypesForDocument.filter((st) => showTechnicalStatus === true || !!st.StatusTypesId);
        }
    }

    showHideDocumentConfigLinks() {
        if (this.selectedDocument) {

            if (this.isNullOrUndefined(this.selectedDocument?.FormatIn?.FormatsId) || this.isNullOrUndefined(this.selectedDocument?.VariantIn?.VariantsId) || this.isNullOrUndefined(this.selectedDocument?.VariantVersionIn?.VariantVersionsId)) {
                this.showDocumentConfigInLinks = false;
            } else {
                this.showDocumentConfigInLinks = true;
            }

            if (this.isNullOrUndefined(this.selectedDocument?.FormatOut?.FormatsId) || this.isNullOrUndefined(this.selectedDocument?.VariantOut?.VariantsId) || this.isNullOrUndefined(this.selectedDocument?.VariantVersionOut?.VariantVersionsId)) {
                this.showDocumentConfigOutLinks = false;
            } else {
                this.showDocumentConfigOutLinks = true;
            }
        }
    }

    removeDocument(document: Document | undefined) {

        if (document === undefined) {
            return;
        }

        const documentIdIndex = this.documentIds.indexOf(document?.DocumentId);
        if (documentIdIndex >= 0) {
            this.documentIds.splice(documentIdIndex, 1);

            const documentIndex = this.documents.map(e => e.DocumentId).indexOf(document?.DocumentId);
            this.documents.splice(documentIndex, 1);
        }
        const interchangeInIdIndex = this.interchangeInIds.indexOf(document.InterchangeIn?.InterchangesId);
        if (interchangeInIdIndex >= 0) {
            this.interchangeInIds.splice(interchangeInIdIndex, 1);

            const documentIndex = this.documents.map(e => e.InterchangeIn?.InterchangesId).indexOf(document.InterchangeIn?.InterchangesId);
            this.documents.splice(documentIndex, 1);
        }

        this.updateUrlAndCleanParams();
        void this.refresh();
    }

    addDocument(documentId: number) {
        const existingDocument = this.documents.find(d => d.DocumentId === documentId);
        if (existingDocument) {
            this.setSelectedDocument(existingDocument);
        }
        else {
            this.documentIds.push(documentId);
            void this.refresh(documentId);
        }
    }

    addInterchange(interchangeId: number) {
        const existingInterchangeId = this.interchangeInIds.findIndex(e => e === interchangeId) >= 0;
        const existingDocument = this.documents.find(e => e.InterchangeIn?.InterchangesId === interchangeId);
        if (existingInterchangeId && existingDocument) {
            this.setSelectedDocument(existingDocument);
        }
        else {
            if (!existingInterchangeId) {
                this.interchangeInIds.push(interchangeId);
            }
            void this.refresh(undefined, interchangeId);
        }
    }

    private async findExceptionInstances(): Promise<void> {
        let result: ExceptionInstance[] = [];
        if (this.selectedDocument?.DocumentId) {
            result = await this.trackingService.getExceptionInstancesForDocument(this.selectedDocument.DocumentId);
        }
        if (this.selectedDocument?.InterchangeIn.InterchangesId) {
            result = result.concat(await this.trackingService.getExceptionInstancesForInterchangeIn(this.selectedDocument.InterchangeIn.InterchangesId));
        }
        // Sort based on ExceptionInstancesId to get them in a chronoligical order
        result = result.sort((x, y) => x.ExceptionInstancesId - y.ExceptionInstancesId);
        this.exceptionInstances = result;
    }

    private async getBatchInfo(): Promise<void> {
        if (this.selectedDocument?.LatestDocumentStatus.StatusTypeName === 'Batching') {
            try {
                const result = await this.batchingService.getBatchInfoForDocument(this.selectedDocument.DocumentId);
                this.batchInfo = result;
            }
            catch {
                // Keep previous batch info on fetch failure.
            }
        }
        else {
            this.batchInfo = null;
        }
    }

    async onReleaseBatch(): Promise<void> {
        try {
            await this.modalService.confirmDirect('Release batch manually', 'Are you sure you want to release this batch immediately?');
            if (this.selectedDocument && this.batchInfo) {
                try {
                    await this.batchingService.releaseDocumentBatch(this.selectedDocument.DocumentId);
                    this.toastService.showToast('Manual batch release request sent...', ToastType.success);
                }
                catch (err) {
                    console.error(err);
                    this.toastService.showToast('Failed to send manual batch release request...', ToastType.error);
                }
            }
        }
        catch {
            // Confirmation was cancelled.
        }
    }

    private getDatewithMS(creationDate: string | number | Date): string {
        const date = new Date(creationDate);
        return this.dateTimeService.getDevExpressDateTimeWithMsFunction(date);
    }

    private async findStatusTypes(): Promise<void> {
        this.statusTypesForDocument = [];
        this.loadingStatusTypes = true;

        let result: DocumentStatusType[] = [];
        if (this.selectedDocument?.DocumentId) {
            const documentStatuses = await this.trackingService.getStatusTypesForDocument(this.selectedDocument.DocumentId);
            result = result.concat(documentStatuses);
        }

        if (this.selectedDocument?.InterchangeIn.InterchangesId) {
            const interchangeInStatuses = await this.trackingService.getStatusTypesForInterchangeIn(this.selectedDocument.InterchangeIn.InterchangesId);
            result = result.concat(interchangeInStatuses);
        }

        this.findStatusTypesInternal(result);

    }

    private findStatusTypesInternal(result: DocumentStatusType[]): void {
        result.forEach(st => {
            st.CreatedDate = new Date(st.CreatedDate);
        });

        this.statusTypesForDocument = result.sort((e1: DocumentStatusType, e2: DocumentStatusType) => e2.Id - e1.Id);

        void this.findExceptionInstances();
        this.showHideTechnicalStatus(this.showTechnicalStatus);
        this.loadingStatusTypes = false;
    }

    async findTrackingFields(): Promise<void> {
        if (this.selectedDocument && !this.selectedDocument['trackingFieldGroups']) {
            this.loadingPanelService.showLoadingPanelDirect('Loading tracking fields...');

            if (this.selectedDocument.DocumentId) {
                try {
                    const result = await this.trackingFieldService.getGroupedDataFieldsForDocument(this.selectedDocument.DocumentId);
                    this.selectedDocument['trackingFieldGroups'] = result;
                }
                catch {
                    this.selectedDocument['trackingFieldGroups'] = [];
                }
            } else {
                this.selectedDocument['trackingFieldGroups'] = [];
            }

            this.loadingPanelService.hideLoadingPanel();
        }
    }

    async findStatusEvents(): Promise<void> {
        const searchObject = {
            StatusEventSubscriptionId: null, StatusEventSubscriptionStatusTypeId: null, DocumentId: null, InterchangeInId: null, FromDate: null, ToDate: null, SearchId: Guid.empty
        } as StatusEventSearchObject;

        if (this.selectedDocument?.DocumentId) {
            searchObject.DocumentId = this.selectedDocument.DocumentId;
        } else {
            searchObject.InterchangeInId = this.selectedDocument?.InterchangeIn.InterchangesId;
        }

        try {
            const result = await this.trackingService.searchStatusEvents(searchObject);
            this.statusEvents = result;
        }
        catch {
            // ignore
        }
    }

    viewRawDocument() {
        if (this.selectedDocument) {
            const settings = new ModalSettingsJsonViewer('Raw document details', null, this.selectedDocument);
            settings.backdrop = 'static';
            settings.windowClass = 'hugeModal';
            void this.modalService.jsonViewer(settings);
        }
    }


    async cancelMessage(): Promise<void> {
        const interchangeInIds: number[] = [];
        const documentIds: number[] = [];
        const interchangeOutIds: number[] = [];

        if (this.selectedDocument?.InterchangeOut?.InterchangesId) {
            interchangeOutIds.push(this.selectedDocument?.InterchangeOut?.InterchangesId);
        }
        else if (this.selectedDocument?.DocumentId) {
            documentIds.push(this.selectedDocument?.DocumentId);
        }
        else if (this.selectedDocument?.InterchangeIn?.InterchangesId) {
            interchangeInIds.push(this.selectedDocument?.InterchangeIn?.InterchangesId);
        }
        try {
            const modalSettings = new ModalSettingsTemplate(null, 'static');
            const componentValues = { interchangeInIds, documentIds, interchangeOutIds };
            await this.modalService.openComponent(CancelMessageDialogComponent, componentValues, modalSettings);

            await this.refresh();
        }
        catch {
            // ignore
        }
    }

    showRawErrorDetails(error: unknown): void {
        const settings = new ModalSettingsJsonViewer('Raw error details', null, error);
        settings.backdrop = 'static';
        settings.windowClass = 'hugeModal';
        void this.modalService.jsonViewer(settings);
    }

    showText(header: string, text: string): void {
        text = this.escapeHtml(text);
        void this.modalService.messageDirect(header, '<pre>' + text + '</pre>').catch(() => { });
    }

    getOutgoingInterchangeLogFileIdentifier(): string {
        return this.selectedDocument?.InterchangeOut?.TransportLogFileGuid ?? this.selectedDocument?.InterchangeOut?.TransportLogFile ?? null;
    }

    getIngoingInterchangeLogFileIdentifier(): string {
        return this.selectedDocument?.InterchangeIn?.TransportLogFileGuid ?? this.selectedDocument?.InterchangeIn?.TransportLogFile ?? null;
    }

    private readonly entityMap: Record<string, string> = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        '\'': '&#39;',
        '/': '&#x2F;',
    };

    escapeHtml(text: string): string {
        return String(text).replace(/[&<>"'/]/g, s => this.entityMap[s]);
    }

    async addToTestCase(): Promise<void> {
        const interchangeInId = this.selectedDocument?.InterchangeIn?.InterchangesId;
        const componentValues: { interchangeInIds: number[]; } = {
            interchangeInIds: interchangeInId !== null && interchangeInId !== undefined ? [interchangeInId] : []
        };

        try {
            const modalSettings = new ModalSettingsTemplate('lg', 'static');
            const result = await this.modalService.openComponent(AddInterchangeToTestCaseDialogComponent, componentValues, modalSettings) as unknown as AddInterchangeToTestCaseDialogResult;

            await this.testToolService.addInterchangesToTestCase(result.testCase.Id, result.interchangeIds, result.selectedOutputType);

            const interchangeCount = result.interchangeIds.length;
            if (interchangeCount < 2) {
                void this.modalService.messageDirect('Interchanges added to test case', `One Interchange added to test case '${result.testCase.Name}'.`).catch(() => { });
            }
            else {
                void this.modalService.messageDirect('Interchanges added to test case', `${interchangeCount} interchanges are added to test case '${result.testCase.Name}'.`).catch(() => { });
            }
        }
        catch (err: unknown) {
            const errorObject = typeof err === 'object' && err !== null && 'error' in err ? (err as { error?: unknown; }) : null;
            const apiErrorText = typeof errorObject?.error === 'string' ? errorObject.error : null;
            const errMsg = apiErrorText ? `No interchanges added to test case.<br><br>${apiErrorText}` : 'No interchanges added to test case';
            void this.modalService.messageDirect('Operation cancelled', errMsg).catch(() => { });
        }
    }

    async runTrackingUiAction(action: TrackingUiAction): Promise<void> {
        const componentValues = {
            trackingUiAction: action,
            trackingActionDocuments: this.selectedDocument ? [this.toTrackingActionDocument(this.selectedDocument)] : []
        };

        try {
            const modalSettings = new ModalSettingsTemplate('lg', 'static');
            await this.modalService.openComponent(RunTrackingUiActionDialogComponent, componentValues, modalSettings);
        }
        catch {
            // Ignore
        }
        void this.refreshSelected();
    }

    private toTrackingActionDocument(document: Document): TrackingActionDocument {
        return !document ? null : {
            InterchangeInId: document.InterchangeIn?.InterchangesId,
            InterchangeInNumber: document.InterchangeIn?.InterchangeNumber,
            InterchangeInOriginatingId: document.InterchangeIn?.OriginatingId,
            TransmissionIdIn: document.InterchangeIn?.TransmissionId,
            DocumentId: document.DocumentId,
            DocumentNumber: document.DocumentNumber,
            DocumentOriginatingId: document.OriginatingId,
            LatestDocumentStatus: document.LatestDocumentStatus?.StatusTypeName,
            PartnerSystem: !document.InterchangeIn?.PartnerSystem ? null : {
                PartnerSystemId: document.InterchangeIn.PartnerSystem.Id,
                Key: document.InterchangeIn.PartnerSystem.Key,
                Name: document.InterchangeIn.PartnerSystem.Name,
            },
            DocumentType: !document.DistributionObject ? null : {
                DocumentTypeId: document.DistributionObject.Id,
                Name: document.DistributionObject.Name,
            },
            PartnerIn: !document.PartnerIn ? null : {
                PartnerId: document.PartnerIn.PartnersId,
                Name: document.PartnerIn.PartnerName,
                PartnerKey: document.PartnerIn.EdiPortalId,
            },
            PartnerOut: !document.PartnerOut ? null : {
                PartnerId: document.PartnerOut.PartnersId,
                Name: document.PartnerOut.PartnerName,
                PartnerKey: document.PartnerOut.EdiPortalId,
            },
            DocumentConfigIn: !(document.FormatIn || document.VariantIn || document.VariantVersionIn) ? null : {
                FormatName: document.FormatIn?.FormatName,
                VariantName: document.VariantIn?.VariantName,
                VariantVersionName: document.VariantVersionIn?.VariantVersionName,
            },
            DocumentConfigOut: !(document.FormatOut && document.VariantOut && document.VariantVersionOut) ? null : {
                FormatName: document.FormatOut?.FormatName,
                VariantName: document.VariantOut?.VariantName,
                VariantVersionName: document.VariantVersionOut?.VariantVersionName,
            },
        } as TrackingActionDocument;
    }

    private updateIdCollections() {
        const allCurrentDocumentIds = this.documents.filter(x => x.DocumentId).map(x => x.DocumentId);
        const allCurrentInterchangeInIds = this.documents.filter(x => !x.DocumentId).map(x => x.InterchangeIn.InterchangesId);

        this.documentIds = allCurrentDocumentIds;
        this.interchangeInIds = allCurrentInterchangeInIds;

        this.ensureDocumentCap();
    }

    private ensureDocumentCap() {
        // Make sure we don't load more documents than allowed...
        if (this.documentIds.length > TrackingService.maxNumberOfDocumentsToShow) {
            this.documentIds = this.documentIds.slice(0, TrackingService.maxNumberOfDocumentsToShow);
        }

        const maxRemainingDocumentsToShow = TrackingService.maxNumberOfDocumentsToShow - this.documentIds.length;

        if (maxRemainingDocumentsToShow > 0 && this.interchangeInIds.length > maxRemainingDocumentsToShow) {
            this.interchangeInIds = this.interchangeInIds.slice(0, maxRemainingDocumentsToShow);
        }

        this.updateUrlAndCleanParams();
    }

    private showInterchangeHasTooManyDocumentsWarning() {
        this.modalService.messageDirect('Attention', 'One or more of the selected interchanges contains more documents than can be displayed.').catch(() => { });
    }

    private updateUrlAndCleanParams() {
        if (this.documentIds.length > 0 && this.interchangeInIds.length > 0) {
            this.updateUrlWithoutNavigation('/tracking/result/details', { documentIds: this.documentIds.join(','), interchangeInIds: this.interchangeInIds.join(','), internalReload: true });
        } else if (this.documentIds.length > 0 && this.interchangeInIds.length < 1) {
            this.updateUrlWithoutNavigation('/tracking/result/details', { documentIds: this.documentIds.join(','), internalReload: true });
        } else if (this.documentIds.length < 1 && this.interchangeInIds.length > 0) {
            this.updateUrlWithoutNavigation('/tracking/result/details', { interchangeInIds: this.interchangeInIds.join(','), internalReload: true });
        }
    }

    private isNullOrUndefined(value: unknown): value is null | undefined {
        return value === null || value === undefined;
    }

    private updateUrlWithoutNavigation(relativeUrl: string, queryParams: Params): void {
        // Create full url as string.
        const url = this.router.createUrlTree([relativeUrl, queryParams]).toString();
        // Update url without navigating, but also replacing the current state on the 'back button stack'.
        this.location.replaceState(url);
    }

    get additionalFilesCount(): number {
        return (this.selectedDocument?.AdditionalBackupFiles?.length || 0) +
            (this.selectedDocument?.InterchangeIn?.AdditionalBackupFiles?.length || 0) +
            (this.selectedDocument?.InterchangeOut?.AdditionalBackupFiles?.length || 0);
    }

    get relatedMessagesCount(): number {
        return this.selectedDocument?.MessageRelations?.length ?? 0;
    }

    getMessageRelations(): MessageRelation[] {
        return this.selectedDocument?.MessageRelations ?? [];
    }

    private loadCustomActionButtons(): void {
        if (this.selectedDocument && this.trackingUiActions.length > 0) {

            const trackingActionDocument = this.toTrackingActionDocument(this.selectedDocument);

            this.trackingUiActions.forEach((t, i) => {
                const state = this.trackingUiActionStates[i];
                state.IsDisabled = true;
                state.Title = 'Loading...';
                void this.loadCustomActionButtonAsync(t, trackingActionDocument, state);
            });
        }
    }

    private async loadCustomActionButtonAsync(trackingUiAction: TrackingUiAction, trackingActionDocument: TrackingActionDocument, state: TrackingUiActionState): Promise<void> {
        const validateResult: TrackingActionValidationResultSingle = await this.trackingService.validateTrackingActionSingle(trackingUiAction.Id, trackingActionDocument);
        state.IsDisabled = !validateResult.IsValid;
        if (validateResult.IsValid) {
            state.Title = trackingUiAction.Description;
        }
        else {
            state.Title = validateResult.Reason;
        }
    }
}
